import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import { adminListContent, adminDeleteContent, adminReorder, adminToggleVisibility, adminUpdateContent, adminListCollection, adminBulkTag, adminSystemContentCounts, adminSystemContentVisibility } from "@/lib/api";
import { CATEGORY_BY_TYPE, CATEGORIES, itemPath } from "@/lib/categories";
import { FilePlus2, Pencil, Trash2, Star, GripVertical, Eye, EyeOff, ExternalLink, Send, Tags, X, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BULK_DIMS = [
  { key: "deities", label: "Deity", coll: "deities" },
  { key: "categories", label: "Category", coll: "categories" },
  { key: "temples", label: "Temple", coll: "temples" },
];

export default function AdminDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") === "draft" ? "draft" : "all");
  const [originFilter, setOriginFilter] = useState("all");
  const [sysCounts, setSysCounts] = useState(null);
  const [sysBusy, setSysBusy] = useState(false);
  const [sysIncludeEdited, setSysIncludeEdited] = useState(false);

  // Bulk-tag mode
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDim, setBulkDim] = useState("deities");
  const [bulkSlug, setBulkSlug] = useState("");
  const [bulkOptions, setBulkOptions] = useState([]);
  const [applying, setApplying] = useState(false);

  const load = () => {
    setLoading(true);
    adminListContent().then(setItems).finally(() => setLoading(false));
    adminSystemContentCounts().then(setSysCounts).catch(() => {});
  };

  useEffect(load, []);

  const runSystemVisibility = async (hidden) => {
    setSysBusy(true);
    try {
      const res = await adminSystemContentVisibility({ collection: "all", hidden, include_edited: hidden ? sysIncludeEdited : true });
      const n = (res.updated?.content || 0) + (res.updated?.hub || 0);
      toast(hidden ? `Hidden ${n} system page${n === 1 ? "" : "s"}` : `Restored ${n} system page${n === 1 ? "" : "s"}`, {
        description: hidden ? "Your edited pages were kept visible unless you included them." : "All system pages are visible again.",
      });
      load();
    } catch (e) {
      toast.error("Could not update", { description: e.response?.data?.detail || e.message });
    } finally { setSysBusy(false); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await adminDeleteContent(id);
    toast("Content deleted", { description: title });
    load();
  };

  const toggle = async (it) => {
    const res = await adminToggleVisibility("content", it.id);
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, hidden: res.hidden } : x)));
    toast(res.hidden ? "Hidden from site" : "Now visible", { description: it.title });
  };

  // One-click publish a draft (sends the item's current fields + status=published,
  // so nothing else is overwritten).
  const publishDraft = async (it) => {
    try {
      await adminUpdateContent(it.id, { ...it, status: "published" });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: "published" } : x)));
      toast.success("Published — now live", { description: it.title });
    } catch (e) {
      toast.error("Could not publish", { description: e.response?.data?.detail || e.message });
    }
  };

  const byType = {};
  items.forEach((i) => (byType[i.type] = (byType[i.type] || 0) + 1));
  const draftCount = items.filter((i) => i.status === "draft").length;

  const setStatus = (v) => {
    setStatusFilter(v);
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("status"); else next.set("status", v);
    setSearchParams(next, { replace: true });
  };

  const visible = useMemo(
    () => items.filter((i) => (typeFilter === "all" || i.type === typeFilter) &&
      (statusFilter === "all" || (statusFilter === "draft" ? i.status === "draft" : i.status !== "draft")) &&
      (originFilter === "all"
        || (originFilter === "system" && i.system_generated && !i.admin_edited)
        || (originFilter === "edited" && i.admin_edited))),
    [items, typeFilter, statusFilter, originFilter]
  );
  const canReorder = typeFilter !== "all" && statusFilter === "all" && originFilter === "all" && !bulkMode;

  // Bulk-tag: load taxonomy options for the chosen dimension.
  useEffect(() => {
    if (!bulkMode) return;
    const coll = BULK_DIMS.find((d) => d.key === bulkDim)?.coll;
    adminListCollection(coll)
      .then((rows) => setBulkOptions(rows.map((r) => ({ slug: r.slug, label: r.name || r.title || r.slug }))))
      .catch(() => setBulkOptions([]));
    setBulkSlug("");
  }, [bulkMode, bulkDim]);

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisibleSelected = visible.length > 0 && visible.every((i) => selected.has(i.id));
  const toggleSelectAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allVisibleSelected) visible.forEach((i) => n.delete(i.id));
    else visible.forEach((i) => n.add(i.id));
    return n;
  });

  const exitBulk = () => { setBulkMode(false); setSelected(new Set()); setBulkSlug(""); };

  // Clear the selection whenever the visible set changes (filter/status change or
  // leaving bulk mode) so admins never apply a tag to now-hidden, off-screen items.
  useEffect(() => { setSelected(new Set()); }, [typeFilter, statusFilter, originFilter, bulkMode]);

  const applyBulk = async () => {
    // Safety net: only ever tag items that are currently visible.
    const visibleIds = new Set(visible.map((i) => i.id));
    const ids = [...selected].filter((id) => visibleIds.has(id));
    if (!bulkSlug || ids.length === 0) { toast.error("Select items and a tag first."); return; }
    setApplying(true);
    try {
      const res = await adminBulkTag(ids, bulkDim, bulkSlug);
      toast.success(`Tagged ${res.updated} item${res.updated === 1 ? "" : "s"}`, { description: `${BULK_DIMS.find((d) => d.key === bulkDim)?.label}: ${res.name}` });
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error("Bulk tag failed", { description: err.response?.data?.detail || err.message });
    } finally { setApplying(false); }
  };

  // Keep a live reference to the currently visible order so we can persist
  // it once a drag gesture ends (onReorder fires continuously while dragging).
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Reorder only rearranges the items of the active type; everything else keeps its place.
  const handleReorder = (next) => {
    const reordered = items.filter((i) => i.type !== typeFilter).concat(next);
    setItems(reordered);
  };

  const persistOrder = async () => {
    try {
      await adminReorder("content", visibleRef.current.map((x) => x.id));
      toast.success("Order updated");
    } catch {
      toast.error("Could not save order");
      load();
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">Content Library</h1>
          <p className="mt-1 text-sm text-ink-3">{items.length} entries · {draftCount} draft{draftCount === 1 ? "" : "s"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatus}>
            <SelectTrigger data-testid="content-status-filter" className="w-40 rounded-none border-line bg-surface"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none border-line bg-surface">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Drafts ({draftCount})</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="content-type-filter" className="w-48 rounded-none border-line bg-surface"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none border-line bg-surface">
              <SelectItem value="all">All types</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.type} value={c.type}>{c.en} ({byType[c.type] || 0})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={originFilter} onValueChange={setOriginFilter}>
            <SelectTrigger data-testid="content-origin-filter" className="w-44 rounded-none border-line bg-surface"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none border-line bg-surface">
              <SelectItem value="all">All origins</SelectItem>
              <SelectItem value="system">System (untouched)</SelectItem>
              <SelectItem value="edited">Edited by me</SelectItem>
            </SelectContent>
          </Select>
          <button data-testid="bulk-tag-toggle" onClick={() => (bulkMode ? exitBulk() : setBulkMode(true))}
            className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm font-medium transition-colors ${bulkMode ? "border-saffron bg-[rgba(199,80,0,0.08)] text-saffron" : "border-line text-ink-2 hover:border-[var(--ink)] hover:text-ink"}`}>
            <Tags className="h-4 w-4" /> {bulkMode ? "Exit bulk tag" : "Bulk tag"}
          </button>
          <Link to="/admin/content/new" data-testid="admin-new-content" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
            <FilePlus2 className="h-4 w-4" /> New Content
          </Link>
        </div>
      </div>

      {sysCounts && (
        <div data-testid="system-content-panel" className="mb-4 border border-line bg-surface p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-ink">System-generated pages</p>
              <p className="mt-1 text-xs text-ink-3">
                Content: <span className="dh-num">{sysCounts.content?.total || 0}</span> system
                (<span className="dh-num">{sysCounts.content?.edited || 0}</span> edited by you,
                <span className="dh-num"> {sysCounts.content?.hidden || 0}</span> hidden) ·
                Knowledge Hub: <span className="dh-num">{sysCounts.hub?.total || 0}</span> system
                (<span className="dh-num">{sysCounts.hub?.hidden || 0}</span> hidden)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-ink-2">
                <input type="checkbox" data-testid="system-include-edited" checked={sysIncludeEdited} onChange={(e) => setSysIncludeEdited(e.target.checked)} />
                Include pages I've edited
              </label>
              <button data-testid="system-hide-btn" onClick={() => runSystemVisibility(true)} disabled={sysBusy}
                className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink disabled:opacity-50">
                <EyeOff className="h-4 w-4" /> Hide system pages
              </button>
              <button data-testid="system-show-btn" onClick={() => runSystemVisibility(false)} disabled={sysBusy}
                className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink disabled:opacity-50">
                <Eye className="h-4 w-4" /> Show all
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkMode && (
        <div data-testid="bulk-tag-bar" className="mb-4 flex flex-wrap items-center gap-3 border border-saffron/40 bg-[rgba(199,80,0,0.04)] p-3">
          <span className="text-sm font-medium text-ink"><span data-testid="bulk-selected-count">{selected.size}</span> selected</span>
          <span className="text-ink-3">·</span>
          <span className="text-sm text-ink-2">Add</span>
          <select data-testid="bulk-dim" value={bulkDim} onChange={(e) => setBulkDim(e.target.value)} className="border border-line bg-parchment px-2 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none">
            {BULK_DIMS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <select data-testid="bulk-slug" value={bulkSlug} onChange={(e) => setBulkSlug(e.target.value)} className="min-w-[12rem] border border-line bg-parchment px-2 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none">
            <option value="">Choose a {BULK_DIMS.find((d) => d.key === bulkDim)?.label.toLowerCase()}…</option>
            {bulkOptions.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
          </select>
          <button data-testid="bulk-apply" onClick={applyBulk} disabled={applying || !bulkSlug || selected.size === 0}
            className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-50">
            <Check className="h-4 w-4" /> {applying ? "Applying…" : "Apply"}
          </button>
          <button onClick={exitBulk} className="ml-auto inline-flex items-center gap-1 text-sm text-ink-3 hover:text-saffron"><X className="h-4 w-4" /> Done</button>
        </div>
      )}

      {canReorder && (
        <p className="mb-3 text-xs text-ink-3">Drag the <GripVertical className="inline h-3.5 w-3.5" /> handle to reorder how <span className="text-saffron">{CATEGORY_BY_TYPE[typeFilter]?.en}</span> appear on the public site. The new order saves automatically.</p>
      )}
      {typeFilter !== "all" && statusFilter !== "all" && !bulkMode && (
        <p className="mb-3 text-xs text-ink-3">Set the status filter back to <span className="text-saffron">All statuses</span> to drag-and-drop reorder these entries.</p>
      )}

      {loading ? (
        <p className="py-16 text-center text-ink-3">…</p>
      ) : (
        <div className="overflow-hidden border border-line bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-surface-alt/50 text-xs uppercase tracking-wider text-ink-3">
              <tr>
                {bulkMode && <th className="w-12 px-3 py-3"><input type="checkbox" data-testid="bulk-select-all" checked={allVisibleSelected} onChange={toggleSelectAll} className="h-4 w-4 accent-[var(--saffron)]" /></th>}
                {canReorder && <th className="w-12 px-2 py-3"></th>}
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Type</th>
                <th className="hidden px-4 py-3 sm:table-cell">Category</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            {canReorder ? (
              <Reorder.Group as="tbody" axis="y" values={visible} onReorder={handleReorder}>
                {visible.map((it) => (
                  <ContentRow key={it.id} it={it} canReorder onPersist={persistOrder}
                    onToggle={toggle} onPublish={publishDraft} onRemove={remove} />
                ))}
              </Reorder.Group>
            ) : (
              <tbody>
                {visible.map((it) => (
                  <ContentRow key={it.id} it={it} canReorder={false}
                    bulkMode={bulkMode} checked={selected.has(it.id)} onToggleCheck={() => toggleSelect(it.id)}
                    onToggle={toggle} onPublish={publishDraft} onRemove={remove} />
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function ContentRow({ it, canReorder, bulkMode, checked, onToggleCheck, onPersist, onToggle, onPublish, onRemove }) {
  const controls = useDragControls();
  const cells = (
    <>
      {bulkMode && (
        <td className="w-12 px-3 py-3 align-middle">
          <input type="checkbox" data-testid={`bulk-check-${it.slug}`} checked={!!checked} onChange={onToggleCheck} className="h-4 w-4 accent-[var(--saffron)]" />
        </td>
      )}
      {canReorder && (
        <td className="w-12 px-2 py-3 align-middle">
          <button
            data-testid={`content-drag-${it.slug}`}
            onPointerDown={(e) => controls.start(e)}
            title="Drag to reorder"
            className="flex touch-none cursor-grab items-center justify-center text-ink-3 transition-colors hover:text-saffron active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {it.featured && <Star className="h-3.5 w-3.5 fill-[var(--gold)] text-[var(--gold)]" />}
          <span className={`font-deva-body ${it.hidden ? "text-ink-3 line-through" : "text-ink"}`}>{it.hindi_title || it.devanagari_title || it.title}</span>
          {it.hidden && <span className="rounded-none border border-line px-1.5 text-[10px] uppercase tracking-wide text-ink-3">Hidden</span>}
          {it.admin_edited ? (
            <span data-testid={`admin-edited-${it.slug}`} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">Edited</span>
          ) : it.system_generated && (
            <span data-testid={`admin-system-${it.slug}`} className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-700">System</span>
          )}
          {it.status === "draft" && <span data-testid={`admin-draft-${it.slug}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">Draft</span>}
        </div>
        <span className="text-xs text-ink-3">{it.english_title || it.title}</span>
      </td>
      <td className="px-4 py-3 text-ink-2">{CATEGORY_BY_TYPE[it.type]?.en || it.type}</td>
      <td className="hidden px-4 py-3 text-ink-2 sm:table-cell">{it.category}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          {it.status === "draft" && (
            <button data-testid={`admin-publish-${it.slug}`} onClick={() => onPublish(it)} title="Publish now"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:bg-emerald-700">
              <Send className="h-3 w-3" /> Publish
            </button>
          )}
          <a href={itemPath(it)} target="_blank" rel="noopener noreferrer" data-testid={`admin-view-${it.slug}`} title="View on live site" className="p-2 text-ink-2 transition-colors hover:text-saffron"><ExternalLink className="h-4 w-4" /></a>
          <button data-testid={`admin-toggle-${it.slug}`} onClick={() => onToggle(it)} title={it.hidden ? "Show" : "Hide"} className="p-2 text-ink-2 transition-colors hover:text-saffron">{it.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          <Link to={`/admin/content/${it.id}`} data-testid={`admin-edit-${it.slug}`} className="p-2 text-ink-2 transition-colors hover:text-saffron"><Pencil className="h-4 w-4" /></Link>
          <button data-testid={`admin-delete-${it.slug}`} onClick={() => onRemove(it.id, it.title)} className="p-2 text-ink-2 transition-colors hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
        </div>
      </td>
    </>
  );

  if (!canReorder) {
    return <tr data-testid={`admin-row-${it.slug}`} className="border-b border-line last:border-0">{cells}</tr>;
  }
  return (
    <Reorder.Item as="tr" value={it} dragListener={false} dragControls={controls} onDragEnd={onPersist}
      data-testid={`admin-row-${it.slug}`} className="border-b border-line bg-surface last:border-0">
      {cells}
    </Reorder.Item>
  );
}
